import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { formatINR, statusTone } from "@/lib/format";
import { statusPalette } from "@/lib/statusColor";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import WoPaymentDialog from "@/components/app/WoPaymentDialog";
import WoDetailDialog from "@/components/app/WoDetailDialog";
import RichEditor from "@/components/app/RichEditor";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, Receipt, Search } from "lucide-react";

const FREELANCER_STATUSES = ["Assigned", "In Progress", "Submitted"];
const MANAGER_STATUSES = ["Assigned", "In Progress", "Submitted", "Completed", "Reassigned", "Cancelled"];
const fmtCreated = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function WorkOrders() {
  const { user } = useAuth();
  const isManager = ["Manager", "TeamLeader", "SuperAdmin"].includes(user.role);
  const STATUSES = isManager ? MANAGER_STATUSES : FREELANCER_STATUSES;
  const [items, setItems] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoiceMap, setInvoiceMap] = useState({});
  const [reviewMap, setReviewMap] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [freelancerFilter, setFreelancerFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [deadlineBefore, setDeadlineBefore] = useState("");
  const [search, setSearch] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [viewWoId, setViewWoId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [woFlSearch, setWoFlSearch] = useState("");
  const [form, setForm] = useState({ project_id: "", freelancer_id: "", title: "", description: "", rate: 0, deadline: "", status: "Assigned" });
  const [reviewFor, setReviewFor] = useState(null);
  const [review, setReview] = useState({ quality_score: 4.5, on_time_score: 4.5, revision_count: 0, reviewer_notes: "" });
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [paymentFor, setPaymentFor] = useState(null);  // WO just marked Completed → prompt
  const [summaryFor, setSummaryFor] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  const openSummary = async (w) => {
    setSummaryFor(w); setSummaryData(null);
    try { const { data } = await api.get(`/work-orders/${w.id}/payment-summary`); setSummaryData(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const confirmDelete = async () => {
    if (!deleteFor) return;
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/work-orders/${deleteFor.id}`);
      const extra = data?.invoices_deleted ? ` (${data.invoices_deleted} invoice removed)` : "";
      toast.success(`Work order deleted${extra}`);
      setDeleteFor(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    setDeleteBusy(false);
  };

  const load = async () => {
    const { data } = await api.get("/work-orders"); setItems(data);
    const p = await api.get("/projects"); setProjects(p.data);
    if (isManager) { const f = await api.get("/freelancers"); setFreelancers(f.data); }
    // Load invoices to know which WOs are already invoiced
    try {
      const pay = await api.get("/payments");
      const m = {}; pay.data.forEach((p) => { if (p.work_order_id) m[p.work_order_id] = p; });
      setInvoiceMap(m);
    } catch {}
    // Load review counts per WO
    try {
      const perf = await api.get("/performance");
      const m = {}; perf.data.forEach((r) => { if (r.work_order_id) m[r.work_order_id] = (m[r.work_order_id] || 0) + 1; });
      setReviewMap(m);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const cancelledCount = items.filter((w) => w.status === "Cancelled").length;
  const shown = items.filter((w) => {
    if (!showCancelled && w.status === "Cancelled" && statusFilter !== "Cancelled") return false;
    if (statusFilter && w.status !== statusFilter) return false;
    if (freelancerFilter && w.freelancer_id !== freelancerFilter) return false;
    if (projectFilter && w.project_id !== projectFilter) return false;
    if (deadlineBefore && w.deadline && w.deadline > deadlineBefore) return false;
    if (search && !(w.title || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    // Submitted first (for reviewers), then by created_at desc
    const aSub = a.status === "Submitted" ? 0 : 1;
    const bSub = b.status === "Submitted" ? 0 : 1;
    if (aSub !== bSub) return aSub - bSub;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
  const fname = (fid) => freelancers.find((x) => x.id === fid)?.name || (user.linked_freelancer_id === fid ? user.name : "—");

  const submitCreate = async (e) => {
    e.preventDefault();
    const proj = projects.find((p) => p.id === form.project_id);
    if (form.deadline && proj?.due_date && form.deadline > proj.due_date) {
      toast.error("Work order due date must be within the project timeline"); return;
    }
    try {
      await api.post("/work-orders", { ...form, rate: Number(form.rate) });
      setNewOpen(false); setWoFlSearch(""); setForm({ project_id: "", freelancer_id: "", title: "", description: "", rate: 0, deadline: "", status: "Assigned" });
      load(); toast.success("Work order created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // When project changes in the New WO form, if it has exactly one assigned freelancer, prefill status Assigned
  const onProjectChange = (pid) => {
    const proj = projects.find((p) => p.id === pid);
    const only = proj?.assigned_freelancer_ids?.length === 1 ? proj.assigned_freelancer_ids[0] : "";
    setForm((f) => ({ ...f, project_id: pid, freelancer_id: only || f.freelancer_id, status: only ? "Assigned" : f.status }));
  };

  const updateStatus = async (wo, status) => {
    try {
      await api.patch(`/work-orders/${wo.id}`, { status });
      load();
      if (status === "Completed" && isManager) {
        setPaymentFor({ ...wo, status });
      } else {
        toast.success("Status updated");
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const convertToInvoice = async (wo) => {
    try {
      await api.post(`/work-orders/${wo.id}/convert-to-invoice`);
      load(); toast.success("Invoice created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openReview = (wo) => {
    setReviewFor(wo);
    setReview({ quality_score: 4.5, on_time_score: 4.5, revision_count: 0, reviewer_notes: "" });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/performance", {
        freelancer_id: reviewFor.freelancer_id, project_id: reviewFor.project_id, work_order_id: reviewFor.id,
        quality_score: Number(review.quality_score), on_time_score: Number(review.on_time_score),
        revision_count: Number(review.revision_count), reviewer_notes: review.reviewer_notes,
      });
      setReviewFor(null); load(); toast.success("Review saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <PageHeader
        title="Work Orders"
        description={isManager ? "Assign, track, invoice and review across the studio." : "Your active work orders. Upload your files inside each assigned project."}
        actions={isManager && <button data-testid="new-work-order-btn" onClick={() => setNewOpen(true)} className="bg-black text-white px-4 py-2 text-sm hard-shadow-hover">+ New Work Order</button>}
      />
      <div className="p-8">
        <div className="mb-4 flex flex-wrap gap-2 items-center" data-testid="wo-filters">
          <Input data-testid="wo-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title…" className="h-9 rounded-lg border-black/15 max-w-xs" />
          <select data-testid="wo-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          {isManager && (
            <select data-testid="wo-freelancer-filter" value={freelancerFilter} onChange={(e) => setFreelancerFilter(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
              <option value="">All Freelancers</option>
              {freelancers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
          <select data-testid="wo-project-filter" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
            <option value="">All Projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Input data-testid="wo-deadline-filter" type="date" value={deadlineBefore} onChange={(e) => setDeadlineBefore(e.target.value)} className="h-9 rounded-lg border-black/15 w-40" title="Deadline before" />
          {(search || statusFilter || freelancerFilter || projectFilter || deadlineBefore) && (
            <button data-testid="wo-filters-clear" onClick={() => { setSearch(""); setStatusFilter(""); setFreelancerFilter(""); setProjectFilter(""); setDeadlineBefore(""); }} className="text-xs underline underline-offset-4">Clear</button>
          )}
          <label className="ml-auto inline-flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="wo-show-cancelled-label">
            <input data-testid="wo-show-cancelled-toggle" type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
            Show Cancelled ({cancelledCount})
          </label>
          <div className="text-xs text-neutral-500 font-mono-tight">{shown.length} / {items.length}</div>
        </div>
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="fms-table w-full" data-testid="work-orders-table">
            <thead><tr>
              <th>Title</th>
              {isManager && <th>Freelancer</th>}
              <th>Project</th><th>Created</th><th>Issued By</th><th>Rate</th><th>Deadline</th><th>Status</th>
              {isManager && <th>Actions</th>}
              {isManager && <th>Review</th>}
              {isManager && <th>Invoice</th>}
            </tr></thead>
            <tbody>
              {shown.map((w) => {
                const isSubmitted = w.status === "Submitted" && isManager;
                return (
                <tr key={w.id} data-testid={`wo-row-${w.id}`} className={isSubmitted ? "bg-gold/10 border-l-4 border-gold" : w.status === "Cancelled" ? "opacity-60" : ""}>
                  <td>
                    {isSubmitted && <span data-testid={`wo-submitted-flag-${w.id}`} className="mr-2 text-[10px] font-mono-tight uppercase tracking-widest bg-gold text-ink px-1.5 py-0.5 rounded">Ready to Review</span>}
                    <button type="button" data-testid={`open-wo-${w.id}`} onClick={() => setViewWoId(w.id)} className="text-left font-medium hover:underline underline-offset-4">{w.title}</button>
                  </td>
                  {isManager && <td className="text-sm">{fname(w.freelancer_id)}</td>}
                  <td className="text-sm" data-testid={`wo-project-${w.id}`}>
                    <div className="text-neutral-800">{w.project_name || "—"}</div>
                    {w.project_number && <div className="font-mono-tight text-xs text-neutral-500">{w.project_number}</div>}
                  </td>
                  <td className="font-mono-tight text-xs text-neutral-600 whitespace-nowrap" data-testid={`wo-created-${w.id}`}>{fmtCreated(w.created_at)}</td>
                  <td className="text-xs text-neutral-600" data-testid={`wo-issued-by-${w.id}`}>{w.issued_by_name || "—"}</td>
                  <td className="font-mono-tight">{formatINR(w.rate)}</td>
                  <td className="font-mono-tight text-neutral-500">{w.deadline || "—"}</td>
                  <td>
                    <select data-testid={`wo-status-${w.id}`} value={w.status}
                      disabled={user?.role === "Freelancer" && w.status === "Cancelled"}
                      title={user?.role === "Freelancer" && w.status === "Cancelled" ? "This work order was cancelled by the manager. Contact them to reopen." : ""}
                      onChange={(e) => updateStatus(w, e.target.value)}
                      style={user?.role === "Freelancer" && w.status === "Cancelled" ? {} : { background: statusPalette(w.status).bg, color: statusPalette(w.status).fg }}
                      className={`border rounded-full px-2 py-1 text-xs font-mono-tight ${user?.role === "Freelancer" && w.status === "Cancelled" ? "border-[#B02A00] text-[#B02A00] bg-neutral-100 cursor-not-allowed" : "border-black/10"}`}>
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  {isManager && (
                    <td className="text-xs whitespace-nowrap space-x-3">
                      <Link data-testid={`wo-edit-${w.id}`} to={`/projects/${w.project_id}?edit_wo=${w.id}`} className="underline underline-offset-4">Edit</Link>
                      <button data-testid={`wo-delete-${w.id}`} onClick={() => setDeleteFor(w)} className="underline underline-offset-4 text-red-600">Delete</button>
                    </td>
                  )}
                  {isManager && (
                    <td className="text-xs">
                      <button data-testid={`wo-review-${w.id}`} onClick={() => openReview(w)} className="inline-flex items-center gap-1 underline underline-offset-4">
                        <Star size={12} /> {reviewMap[w.id] ? `${reviewMap[w.id]} · Add` : "Add"}
                      </button>
                    </td>
                  )}
                  {isManager && (
                    <td className="text-xs">
                      {invoiceMap[w.id]
                        ? <button data-testid={`wo-summary-${w.id}`} onClick={() => openSummary(w)} className="font-mono-tight underline underline-offset-4">{invoiceMap[w.id].invoice_number}</button>
                        : <button data-testid={`wo-invoice-${w.id}`} onClick={() => convertToInvoice(w)} className="inline-flex items-center gap-1 underline underline-offset-4"><Receipt size={12} /> Convert</button>}
                      <button data-testid={`wo-paysummary-${w.id}`} onClick={() => openSummary(w)} className="ml-2 text-[10px] text-royal-700 underline underline-offset-4">Summary</button>
                    </td>
                  )}
                </tr>
                );
              })}
              {shown.length === 0 && <tr><td colSpan={isManager ? 11 : 7} className="text-center text-neutral-500 py-8">No work orders.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <WoPaymentDialog wo={paymentFor} onClose={() => setPaymentFor(null)} onDone={load} />
      <WoDetailDialog woId={viewWoId} onClose={() => setViewWoId(null)} />

      <Dialog open={!!summaryFor} onOpenChange={(o) => !o && setSummaryFor(null)}>
        <DialogContent className="rounded-[1.25rem] max-w-lg" data-testid="wo-summary-dialog">
          {summaryFor && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Payment summary — «{summaryFor.title}»</DialogTitle></DialogHeader>
              {!summaryData ? <div className="text-sm text-neutral-500">Loading…</div> : (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div><div className="label-caps">Total</div><div className="font-mono-tight text-xl">{formatINR(summaryData.total)}</div></div>
                    <div><div className="label-caps">Invoiced</div><div className="font-mono-tight text-xl">{formatINR(summaryData.invoiced)}</div></div>
                    <div><div className="label-caps">% Processed</div><div className="font-mono-tight text-xl text-royal-800">{summaryData.pct_processed}%</div></div>
                    <div><div className="label-caps">% Remaining</div><div className="font-mono-tight text-xl text-[#B02A00]">{summaryData.pct_remaining}%</div></div>
                    <div className="col-span-2"><div className="label-caps">Remaining balance</div><div className="font-mono-tight text-xl">{formatINR(summaryData.remaining)}</div></div>
                  </div>
                  <div>
                    <div className="label-caps mb-1">Invoice history</div>
                    <table className="w-full text-xs">
                      <thead><tr className="text-neutral-500 font-mono-tight"><th className="text-left py-1">Invoice</th><th className="text-left">Amount</th><th className="text-left">Paid</th><th className="text-left">Status</th><th className="text-left">Created</th></tr></thead>
                      <tbody>
                        {summaryData.invoices.map((i) => (
                          <tr key={i.id} className="border-t border-black/5" data-testid={`summary-inv-${i.id}`}>
                            <td className="py-1 font-mono-tight">{i.invoice_number}</td>
                            <td className="font-mono-tight">{formatINR(i.amount)}</td>
                            <td className="font-mono-tight">{formatINR(i.amount_paid || 0)}</td>
                            <td className="font-mono-tight">{i.status}</td>
                            <td className="font-mono-tight text-neutral-500">{(i.created_at || "").slice(0, 10)}</td>
                          </tr>
                        ))}
                        {summaryData.invoices.length === 0 && <tr><td colSpan={5} className="text-center text-neutral-500 py-4">No invoices on this work order yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {summaryData.remaining > 0 && (
                    <button data-testid="process-remaining" onClick={() => { setPaymentFor({ ...summaryFor, preset: { mode: "amount", amount: summaryData.remaining, percentage: 100 } }); setSummaryFor(null); }} className="w-full btn-primary">
                      Process remaining ₹{summaryData.remaining.toLocaleString()}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && !deleteBusy && setDeleteFor(null)}>
        <DialogContent className="rounded-[1.25rem] max-w-md" data-testid="delete-wo-dialog">
          {deleteFor && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl text-red-600">Delete «{deleteFor.title}»?</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="text-ink/80">This is permanent. Any invoice linked to this work order will also be deleted.</div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-mono-tight">
                  ⚠ Deleting this work order will also remove its linked invoice{invoiceMap[deleteFor.id] ? ` (${invoiceMap[deleteFor.id].invoice_number})` : ""}. This cannot be undone.
                </div>
                <div className="flex gap-3 pt-2">
                  <button data-testid="delete-wo-cancel" disabled={deleteBusy} onClick={() => setDeleteFor(null)} className="flex-1 rounded-full border border-black/15 py-2.5 text-sm font-semibold hover:bg-cream-2 disabled:opacity-50">Keep work order</button>
                  <button data-testid="delete-wo-confirm" disabled={deleteBusy} onClick={confirmDelete} className="flex-1 rounded-full bg-red-600 text-white py-2.5 text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                    {deleteBusy ? "Deleting…" : "Yes, delete permanently"}
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={(o) => { setNewOpen(o); if (!o) setWoFlSearch(""); }}>
        <DialogContent className="rounded-none border-2 border-black">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Work Order</DialogTitle></DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div><label className="label-caps block mb-1">Project</label>
              <select required value={form.project_id} onChange={(e) => onProjectChange(e.target.value)} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                <option value="">Select project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1">Freelancer</label>
              <div className="relative">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input data-testid="new-wo-freelancer-search" value={woFlSearch} onChange={(e) => setWoFlSearch(e.target.value)} placeholder="Search freelancer…" className="pl-8 rounded-none border-black h-9" />
              </div>
              <div className="border border-black mt-1 max-h-40 overflow-auto">
                {(() => {
                  const matches = freelancers.filter((f) =>
                    !woFlSearch || (f.name || "").toLowerCase().includes(woFlSearch.toLowerCase()) || (f.email || "").toLowerCase().includes(woFlSearch.toLowerCase()));
                  return (<>
                    {matches.length === 0 && <div className="p-2 text-xs text-neutral-500">No freelancers match your search.</div>}
                    {matches.map((f) => (
                      <label key={f.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-cream-2 ${form.freelancer_id === f.id ? "bg-royal-700/10" : ""}`}>
                        <input type="radio" name="new-wo-fl" value={f.id} checked={form.freelancer_id === f.id} onChange={() => setForm({ ...form, freelancer_id: f.id })} data-testid={`new-wo-fl-${f.id}`} />
                        <span>{f.name}</span><span className="text-xs text-neutral-500">· {f.email}</span>
                      </label>
                    ))}
                  </>);
                })()}
              </div>
            </div>
            <div><label className="label-caps block mb-1">Title</label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-black" /></div>
            <div><label className="label-caps block mb-1">Description</label><RichEditor testid="wo-new-desc" value={form.description_html || ""} onChange={(h) => setForm({ ...form, description_html: h })} placeholder="Brief, steps, references…" minHeight={110} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="label-caps block mb-1">Rate (INR)</label><Input required type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="rounded-none border-black" /></div>
              <div><label className="label-caps block mb-1">Deadline</label>
                <Input data-testid="wo-deadline" type="date" value={form.deadline}
                  max={projects.find((p) => p.id === form.project_id)?.due_date || undefined}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="rounded-none border-black" />
                {form.project_id && projects.find((p) => p.id === form.project_id)?.due_date && (
                  <div className="text-[10px] text-neutral-500 font-mono-tight mt-1">Project ends {projects.find((p) => p.id === form.project_id).due_date}</div>
                )}
              </div>
              <div><label className="label-caps block mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <DialogFooter><button type="submit" data-testid="submit-new-wo" disabled={!form.freelancer_id} className="bg-black text-white px-4 py-2 text-sm disabled:opacity-50">Create</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="rounded-none border-2 border-black">
          {reviewFor && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Review Work Order</DialogTitle></DialogHeader>
              <form onSubmit={submitReview} className="space-y-3">
                <div className="text-xs text-neutral-500 font-mono-tight">{fname(reviewFor.freelancer_id)} · {reviewFor.title}</div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="label-caps block mb-1">Quality</label><Input data-testid="review-quality" type="number" step="0.1" min="0" max="5" value={review.quality_score} onChange={(e) => setReview({ ...review, quality_score: e.target.value })} className="rounded-none border-black" /></div>
                  <div><label className="label-caps block mb-1">On-Time</label><Input data-testid="review-ontime" type="number" step="0.1" min="0" max="5" value={review.on_time_score} onChange={(e) => setReview({ ...review, on_time_score: e.target.value })} className="rounded-none border-black" /></div>
                  <div><label className="label-caps block mb-1">Revisions</label><Input type="number" min="0" value={review.revision_count} onChange={(e) => setReview({ ...review, revision_count: e.target.value })} className="rounded-none border-black" /></div>
                </div>
                <div><label className="label-caps block mb-1">Notes</label><Textarea value={review.reviewer_notes} onChange={(e) => setReview({ ...review, reviewer_notes: e.target.value })} className="rounded-none border-black" /></div>
                <DialogFooter><button data-testid="submit-inline-review" type="submit" className="bg-black text-white px-4 py-2 text-sm">Save Review</button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
